import type { MouseEvent, ReactNode } from 'react';
import {
  TEMPLATE_AUDIENCES,
  TEMPLATE_AUDIENCE_LABELS,
  templateAudienceOf,
  type TemplateKind,
  type TemplateRow,
  type TemplateVersionRow,
} from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { SearchInput } from '../../../../components/Input';
import { Segmented } from '../../../../components/Segmented';
import { SkeletonLines } from '../../../../components/Skeleton';
import { formatDate, formatDateTimeLong, formatNumber } from '../../../../lib/format';
import styles from './TemplateList.module.css';

/** Templates whose name or subject holds the search, any case. */
export function matchingTemplates(rows: readonly TemplateRow[], query: string): readonly TemplateRow[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') return rows;
  return rows.filter((row) => row.name.toLowerCase().includes(needle) || row.subject.toLowerCase().includes(needle));
}

/**
 * ADMIN-09's left column (Option A): the team's / system switch with how many of each match the search, the list
 * (system emails grouped by who they go to, so the 50-odd can be found), and the chosen one's versions, one line each,
 * each opening to read.
 */
export function TemplateList({
  rows,
  kind,
  query,
  selectedId,
  versions,
  viewingVersion,
  onKind,
  onQuery,
  onSelect,
  onVersion,
}: {
  readonly rows: readonly TemplateRow[] | undefined;
  readonly kind: TemplateKind;
  readonly query: string;
  readonly selectedId: string | null;
  readonly versions: readonly TemplateVersionRow[] | undefined;
  readonly viewingVersion: number | null;
  readonly onKind: (kind: TemplateKind) => void;
  readonly onQuery: (query: string) => void;
  readonly onSelect: (row: TemplateRow, event: MouseEvent<HTMLButtonElement>) => void;
  readonly onVersion: (version: TemplateVersionRow | null) => void;
}): ReactNode {
  const matching = matchingTemplates(rows ?? [], query);
  const shown = matching.filter((row) => row.kind === kind);
  const countOf = (of: TemplateKind): number | undefined => (rows ? matching.filter((row) => row.kind === of).length : undefined);

  return (
    <section className={styles.pane} aria-label="Templates">
      <Segmented
        label="Kind"
        value={kind}
        options={[
          { value: 'custom', label: "The team's", ...(countOf('custom') !== undefined ? { count: countOf('custom') } : {}) },
          { value: 'system', label: 'System emails', ...(countOf('system') !== undefined ? { count: countOf('system') } : {}) },
        ]}
        onChange={onKind}
      />
      <SearchInput aria-label="Find a template" placeholder="Find a template" value={query} onChange={(event) => onQuery(event.target.value)} onClear={() => onQuery('')} />
      {!rows ? (
        <SkeletonLines count={6} />
      ) : shown.length === 0 ? (
        <EmptyList kind={kind} query={query} onClear={() => onQuery('')} />
      ) : kind === 'custom' ? (
        <ul className={styles.list} aria-label="The team's templates">
          {shown.map((row) => (
            <Item key={row.id} row={row} isSelected={row.id === selectedId} onSelect={onSelect} />
          ))}
        </ul>
      ) : (
        <div className={styles.groups}>
          {TEMPLATE_AUDIENCES.map((audience) => {
            const inGroup = shown.filter((row) => templateAudienceOf(row) === audience);
            if (inGroup.length === 0) return null;
            return (
              <div key={audience} className={styles.group}>
                <h3 className={styles.groupTitle}>
                  {TEMPLATE_AUDIENCE_LABELS[audience]} <span className={styles.count}>{formatNumber(inGroup.length)}</span>
                </h3>
                <ul className={styles.list} aria-label={TEMPLATE_AUDIENCE_LABELS[audience]}>
                  {inGroup.map((row) => (
                    <Item key={row.id} row={row} isSelected={row.id === selectedId} onSelect={onSelect} />
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
      {selectedId !== null && versions && versions.length > 0 && <Versions versions={versions} viewing={viewingVersion} onVersion={onVersion} />}
    </section>
  );
}

function Item({ row, isSelected, onSelect }: { readonly row: TemplateRow; readonly isSelected: boolean; readonly onSelect: (row: TemplateRow, event: MouseEvent<HTMLButtonElement>) => void }): ReactNode {
  return (
    <li>
      <button type="button" className={styles.item} aria-current={isSelected || undefined} onClick={(event) => onSelect(row, event)}>
        <span className={styles.nameRow}>
          <span className={styles.name}>{row.name}</span>
          {!row.isActive && <span className={styles.off}>Off</span>}
        </span>
        <span className={styles.meta}>
          {formatNumber(row.usage.total)} {row.usage.total === 1 ? 'use' : 'uses'} · {formatNumber(row.usage.last30Days)} in 30 days · v{row.version}
        </span>
      </button>
    </li>
  );
}

function EmptyList({ kind, query, onClear }: { readonly kind: TemplateKind; readonly query: string; readonly onClear: () => void }): ReactNode {
  if (query.trim() !== '') {
    return (
      <div className={styles.empty}>
        <p className={styles.emptyTitle}>Nothing matches “{query.trim()}”</p>
        <p className={styles.meta}>Try part of the name or the subject, or look in the other kind.</p>
        <Button variant="quiet" size="sm" onClick={onClear}>
          Clear the search
        </Button>
      </div>
    );
  }
  return (
    <div className={styles.empty}>
      <p className={styles.emptyTitle}>{kind === 'custom' ? 'No templates of your own yet' : 'No system emails'}</p>
      {kind === 'custom' && <p className={styles.meta}>Make one for the messages you send most: an interview reminder, a polite chase. Use New template.</p>}
    </div>
  );
}

function Versions({ versions, viewing, onVersion }: { readonly versions: readonly TemplateVersionRow[]; readonly viewing: number | null; readonly onVersion: (version: TemplateVersionRow | null) => void }): ReactNode {
  const latest = versions[0]?.version;
  return (
    <div className={styles.versions}>
      <h3 className={styles.groupTitle}>Versions of this one</h3>
      <ul className={styles.versionList} aria-label="Versions of this one">
        {versions.map((version) => {
          const isLatest = version.version === latest;
          const isOpen = viewing === version.version || (viewing === null && isLatest);
          return (
            <li key={version.version}>
              <button
                type="button"
                className={styles.version}
                aria-current={isOpen || undefined}
                title={formatDateTimeLong(version.createdAt)}
                onClick={() => onVersion(isLatest ? null : version)}
              >
                <b>v{version.version}</b> · {formatDate(version.createdAt)} · {version.createdByName ?? 'The system'}
                {isLatest ? ' · now' : ''}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
